import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import AdminSubpage from "../components/AdminSubpage";
import { auctionDepositsAPI } from "../services/api";
import Pagination from "../components/Pagination";
import "../components/Auctions.css";

const AuctionDepositsAdminPage = () => {
  const [deposits, setDeposits] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [rejectNote, setRejectNote] = useState({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  const load = async (page = 1) => {
    setLoading(true);
    try {
      const res = await auctionDepositsAPI.getAll({
        status: status || undefined,
        search: search || undefined,
        page,
        per_page: 20,
      });
      setDeposits(res.data.data || []);
      setCounts(res.data.counts || {});
      setPagination(res.data.pagination || pagination);
    } catch {
      toast.error("Failed to load deposits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const review = async (depositId, action) => {
    const note = rejectNote[depositId];
    if (action === "reject" && !note) {
      toast.error("Rejection reason required");
      return;
    }
    try {
      await auctionDepositsAPI.review(depositId, { action, admin_note: note });
      toast.success(action === "confirm" ? "Deposit confirmed" : "Deposit rejected");
      load(pagination.current_page);
    } catch (err) {
      toast.error(err.response?.data?.message || "Action failed");
    }
  };

  return (
    <AdminSubpage title="Auction deposits" backTo="/auctions">
      <div className="admin-page-panel modal-content">
        <div className="auction-deposit-counts">
          <span><strong>{counts.pending ?? 0}</strong> pending</span>
          <span><strong>{counts.confirmed ?? 0}</strong> confirmed</span>
          <span><strong>{counts.rejected ?? 0}</strong> rejected</span>
        </div>

        <div className="auctions-toolbar">
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
          <input
            className="auction-reject-input"
            style={{ flex: 1, minWidth: 180 }}
            placeholder="Search customer, auction, reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(1)}
          />
          <button type="button" className="btn btn-secondary" onClick={() => load(1)}>
            Search
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading…</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Auction</th>
                  <th>Max bid</th>
                  <th>Deposit</th>
                  <th>Reference</th>
                  <th>Receipt</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deposits.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", color: "var(--text-muted)" }}>
                      No deposits found.
                    </td>
                  </tr>
                ) : null}
                {deposits.map((d) => (
                  <tr key={d.id} className={`table-row-status table-row-status-${d.status === "confirmed" ? "ended" : d.status === "rejected" ? "cancelled" : "scheduled"}`}>
                    <td>
                      {d.user ? (
                        <>
                          <Link to={`/customers/${d.user.id}/edit`}>
                            {d.user.first_name} {d.user.last_name}
                          </Link>
                          <br />
                          <small style={{ color: "var(--text-muted)" }}>{d.user.email}</small>
                        </>
                      ) : "—"}
                    </td>
                    <td>
                      {d.auction ? (
                        <Link to={`/auctions/${d.auction.id}/edit`}>{d.auction.title}</Link>
                      ) : "—"}
                    </td>
                    <td>AED {d.max_bid_amount}</td>
                    <td>AED {d.deposit_amount}</td>
                    <td>{d.transfer_reference}</td>
                    <td>
                      {d.receipt_url ? (
                        <a href={d.receipt_url} target="_blank" rel="noreferrer">View</a>
                      ) : "—"}
                    </td>
                    <td>
                      <span className={`auction-status-badge auction-status-${d.status === "confirmed" ? "ended" : d.status === "rejected" ? "cancelled" : "scheduled"}`}>
                        {d.status}
                      </span>
                    </td>
                    <td>
                      {d.status === "pending" ? (
                        <div className="actions">
                          <button type="button" className="btn btn-success btn-sm" onClick={() => review(d.id, "confirm")}>
                            Confirm ✓
                          </button>
                          <input
                            className="auction-reject-input"
                            placeholder="Reject reason"
                            value={rejectNote[d.id] || ""}
                            onChange={(e) => setRejectNote({ ...rejectNote, [d.id]: e.target.value })}
                          />
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => review(d.id, "reject")}>
                            Reject ✗
                          </button>
                        </div>
                      ) : d.admin_note ? (
                        <small>{d.admin_note}</small>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.last_page}
          onPageChange={load}
        />
      </div>
    </AdminSubpage>
  );
};

export default AuctionDepositsAdminPage;
